import React from 'react'
import { styled, SxProps, Theme } from '@mui/material/styles'
import { FormHelperText } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'

interface CommonPropsType {
  className?: string
  endIconComponent?: React.ReactNode
  isDisabled?: boolean
  label: string | number
  name?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onClick?: () => void
  onClickIcon?: () => void
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  startIconComponent?: React.ReactNode
  shouldShrinkLabel?: boolean
  sx?: SxProps<Theme>
  iconSx?: SxProps<Theme>
  value: string | boolean | number | undefined
  error?: boolean
  helperText?: string
}

interface InputPropsType {
  type?: string
}

interface TextAreaPropsType {
  isMultiline?: boolean
  rows?: number
  minRows?: number
  maxRows?: number
}

type FormInputPropsType = CommonPropsType & InputPropsType & TextAreaPropsType

const CustomInputField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== 'isMultiline' && prop !== 'hasStartIcon',
})<{
  isMultiline: boolean
  value: string | boolean
  readOnly: boolean
  hasStartIcon: boolean
}>(({ theme, isMultiline, readOnly, value, hasStartIcon }) => ({
  minHeight: '80px',
  minWidth: '100px',
  color: '#7D7D7D',
  padding: '12px',
  paddingTop: isMultiline ? (value ? '8px' : '3px') : '12px',
  backgroundColor: 'rgb(8,12,20,0.05)',
  borderColor: '#365F91',
  border: '1.2px solid transparent',
  background: `
      linear-gradient(rgb(8, 12, 20), rgb(8, 12, 20)) padding-box,
      linear-gradient(180deg, #365f91 30%, #365f9133 100%) border-box
    `,
  borderImageSlice: 1,
  borderRadius: '40px',
  '& .MuiInputBase-root': {
    pointerEvents: readOnly ? 'none' : 'auto',
    '&:focus': {
      boxShadow: 'none',
    },
    '&:hover': {
      boxShadow: 'none',
    },
  },
  '& .MuiInputBase-input': {
    color: 'white',
    fontSize: isMultiline ? '16px' : '18px',
    paddingLeft: isMultiline ? '4px' : hasStartIcon ? '3px' : '16px',
    paddingTop: isMultiline
      ? '0'
      : value
        ? '20px !important'
        : '15px !important',
    textAlign: 'justify',
    pointerEvents: readOnly ? 'none' : 'auto',
  },
  '& .MuiInputLabel-root': {
    color: '#7D7D7D !important',
    padding: '6px 0 0 28px ',
    transform: 'translate(0, +23px)',
    pointerEvents: readOnly ? 'none' : 'auto',
    paddingLeft: hasStartIcon ? '60px' : '28px',
  },
  '& .MuiInputLabel-shrink': {
    color: '#7D7D7D !important',
    fontSize: '12px',
    transform: 'translate(0, -1px)',
    padding: hasStartIcon ? '16px 0 0 60px' : '16px 0 0 28px',
  },
  '& .MuiInputLabel-outlined.Mui-focused': {
    color: 'white !important',
  },
  '& .MuiFilledInput-root': {
    backgroundColor: 'transparent !important',
    '&:before': {
      borderBottom: 'none !important',
      backgroundColor: 'transparent !important',
    },
    '&:after': {
      borderBottom: 'none !important',
      backgroundColor: 'transparent !important',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.Mui-focused:after': {
      color: '#7D7D7D',
      borderBottom: 'none',
    },
    '& .Mui-disabled': {
      color: '#808080 !important',
      WebkitTextFillColor: '#808080',
    },
  },
  '&::placeholder': {
    color: '#7D7D7D',
    opacity: 1,
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}))

const FormInput = ({
  endIconComponent,
  error,
  helperText,
  isDisabled,
  isMultiline = false,
  label,
  rows,
  minRows,
  maxRows,
  name,
  placeholder,
  onChange,
  onClick,
  onClickIcon,
  readOnly = false,
  required,
  startIconComponent,
  sx,
  iconSx,
  type,
  value,
  ...props
}: FormInputPropsType) => {
  const handleMouseDownInput = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const shouldMoveIconToTop = isMultiline && !!value

  return (
    <div className="relative w-full">
      <CustomInputField
        error={!!error}
        type={type}
        label={label}
        value={String(value)}
        name={name}
        variant="filled"
        disabled={isDisabled}
        onChange={onChange}
        onClick={onClick}
        placeholder={placeholder}
        multiline={isMultiline}
        isMultiline={isMultiline}
        minRows={minRows}
        rows={rows}
        maxRows={maxRows}
        required={required}
        readOnly={readOnly}
        sx={sx}
        fullWidth
        hasStartIcon={!!startIconComponent}
        InputProps={{
          startAdornment: startIconComponent && (
            <InputAdornment
              position="start"
              sx={{
                marginBottom: value ? '15px !important' : '10px !important',
              }}
            >
              {startIconComponent ? startIconComponent : null}
            </InputAdornment>
          ),
          endAdornment: endIconComponent && (
            <InputAdornment
              position="end"
              sx={{
                position: shouldMoveIconToTop ? 'absolute' : 'relative',
                ...(isMultiline && { marginBottom: '18px' }),
                ...(shouldMoveIconToTop && { top: '14px', right: '16px' }),
                ...iconSx,
              }}
            >
              <IconButton
                aria-label="toggle password visibility"
                onClick={onClickIcon}
                onMouseDown={handleMouseDownInput}
                edge="end"
                sx={{
                  cursor: 'pointer',
                  color: 'white',
                  pointerEvents: 'auto',
                  ...(isMultiline &&
                    !shouldMoveIconToTop && { marginTop: '18px' }),
                }}
              >
                {endIconComponent ? endIconComponent : null}
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: value || value === 0 ? true : false,
        }}
        {...props}
      />
      <FormHelperText
        error
        sx={{
          height: '6px',
          marginLeft: '14px',
          marginTop: '4px',
          color: '#f44336',
        }}
      >
        {error && helperText && helperText}
      </FormHelperText>
    </div>
  )
}

export default FormInput
